import { slugify } from "./slugify"

/** Keys owned by this app in localStorage */
export const PULPIT_STORAGE_KEYS = {
  TRIP_DATA: "tripData",
  CAB_SEARCH_RESULT: "cabSearchResult",
  PREFILL_FROM_CITY: "pulpit_prefill_from_city",
  RECENT_SEARCHES: "pulpit_recent_searches",
}

export function clearSessionStorageSafe() {
  try {
    if (typeof sessionStorage !== "undefined") sessionStorage.clear()
  } catch (_) {
    /* ignore */
  }
}

export function removeCabSearchResult() {
  try {
    localStorage.removeItem(PULPIT_STORAGE_KEYS.CAB_SEARCH_RESULT)
  } catch (_) {
    /* ignore */
  }
}

export function removeTripData() {
  try {
    localStorage.removeItem(PULPIT_STORAGE_KEYS.TRIP_DATA)
  } catch (_) {
    /* ignore */
  }
}

export function removePrefillFromCity() {
  try {
    localStorage.removeItem(PULPIT_STORAGE_KEYS.PREFILL_FROM_CITY)
  } catch (_) {
    /* ignore */
  }
}

/** tripData + cabSearchResult (booking / fare flow) */
export function clearBookingFlowStorage() {
  removeTripData()
  removeCabSearchResult()
}

/**
 * Full reset of app client storage. Use on payment success, logout-like flows, or session expiry redirect.
 * @param {{ keepRecentSearches?: boolean }} options
 */
export function clearAllPulpitClientStorage(options = {}) {
  const { keepRecentSearches = false } = options
  clearBookingFlowStorage()
  removePrefillFromCity()
  if (!keepRecentSearches) {
    try {
      localStorage.removeItem(PULPIT_STORAGE_KEYS.RECENT_SEARCHES)
    } catch (_) {
      /* ignore */
    }
  }
  clearSessionStorageSafe()
}

/**
 * Whether stored outstation trip matches route URL slugs (e.g. ahmedabad → vadodara).
 */
export function tripDataMatchesRouteSlugs(tripParsed, fromSlug, toSlug) {
  if (!tripParsed || tripParsed.trip_type !== 3 || !fromSlug || !toSlug) return false
  const pf = slugify(String(tripParsed.pickup_city || "").trim())
  const pt = slugify(String(tripParsed.drop_city || "").trim())
  const a = String(fromSlug).toLowerCase().trim()
  const b = String(toSlug).toLowerCase().trim()
  return pf === a && pt === b
}

/**
 * Parse tripData from localStorage or return null.
 */
export function readTripDataParsed() {
  try {
    const raw = localStorage.getItem(PULPIT_STORAGE_KEYS.TRIP_DATA)
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

/**
 * Call when entering a /routes/:from-to-to-cab page. Clears sessionStorage always;
 * clears booking storage if trip does not match this route; always drops stale cabSearchResult.
 */
export function reconcileStorageForRoutePage(fromSlug, toSlug) {
  clearSessionStorageSafe()

  const parsed = readTripDataParsed()
  const match = tripDataMatchesRouteSlugs(parsed, fromSlug, toSlug)

  // IMPORTANT:
  // Do not remove cabSearchResult when tripData matches this route.
  // OutstationForm already calls get_trip_fare once and stores the response.
  // If we remove cabSearchResult here, Cab page has no stored fare result and
  // another get_trip_fare call can happen, creating duplicate visitor entries.
  if (!match) {
    clearBookingFlowStorage()
  }

  return { matchedRoute: match }
}

/* ────────────────────────────────────────────────────────────────────────────
 * Last booking receipt.
 *
 * clearBookingFlowStorage() wipes tripData on the confirmation page so the back
 * button can't re-trigger payment. That protection is right, but it is also why
 * a refresh of /booking-success showed "we couldn't find your booking" to
 * someone who had just paid. This is a tiny, separate record — a reference and
 * where the updates are going — kept precisely so that page can stay reassuring.
 * It holds no payment data.
 * ──────────────────────────────────────────────────────────────────────────── */
const LAST_BOOKING_KEY = "pulpit_last_booking"

export function saveLastBooking({ bookingId, tripRef, phone, email, total } = {}) {
  try {
    if (!bookingId && !tripRef) return
    localStorage.setItem(
      LAST_BOOKING_KEY,
      JSON.stringify({ bookingId: bookingId || null, tripRef: tripRef || null, phone: phone || null, email: email || null, total: total || null, at: Date.now() })
    )
  } catch (_) {
    /* ignore */
  }
}

/** Expires after 24h — a week-old reference on a fresh visit would confuse. */
export function readLastBooking() {
  try {
    const raw = JSON.parse(localStorage.getItem(LAST_BOOKING_KEY) || "null")
    if (!raw) return null
    if (Date.now() - (raw.at || 0) > 86400000) {
      localStorage.removeItem(LAST_BOOKING_KEY)
      return null
    }
    return raw
  } catch (_) {
    return null
  }
}
